import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { useAiqmUploadProgress } from "@hook/use-aiqm-upload-progress";
import {
    PhoneCallIcon,
    SearchIcon,
    ClockIcon,
    Loader2Icon,
    UploadIcon,
    XIcon,
    FileAudioIcon,
    CheckCircle2Icon,
    AlertCircleIcon,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    SlidersHorizontalIcon,
    CheckIcon,
    TagIcon,
    PencilIcon,
    UserPlusIcon,
    UserCheckIcon,
    PlusIcon,
    BookOpenIcon,
    BarChart3Icon,
    type LucideIcon,
} from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Card } from "@component/card";
import { Button } from "@component/button";
import BulkActionBar from "@component/bulk-action-bar";
import { Pagination } from "@component/pagination";
import { Input } from "@component/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import {
    getCallSessions,
    uploadAudioFiles,
    updateCallTags,
    listAiqmUploaders,
    listAiqmTrainees,
    assignCallHandler,
    bulkAssignCallHandler,
    bulkAssignTags,
    renameCallSession,
} from "@service/aiqm";
import type { CallSessionListResponse } from "@service/aiqm";
import {
    KB_AIQM_BATCH_KNOWLEDGE_ENABLED,
    listAllKbDocuments,
    type KbDocument,
} from "@service/knowledge-base";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@component/popover";
import type {
    AiqmTrainee,
    AiqmUploader,
    CallSessionListItem,
    CallSessionStatus,
} from "@type/aiqm";
import type { RubricCriterion } from "@type/training";
import {
    DEFAULT_RUBRIC,
    SUPERBANK_LIVECHAT_RUBRIC,
    SUPERBANK_LIVECHAT_TEMPLATE,
} from "@type/training";
import { isSuperbankCompany } from "@constant/company";
import { RubricEditor } from "@container/training/rubric-editor";
import { useAiqmUpload } from "@context/aiqm_upload_context";
import { TrainingErrorCard } from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import AiqmSentimentInsights from "@container/aiqm-sentiment";
import TagInput from "@container/aiqm-sentiment/tag-input";
import { listAiqmTags } from "@service/aiqm/sentiment";

// Feature flags for the Insights tab. The tab is only shown when the UI is
// enabled AND it has a working data source — either mock data, or a backend that
// has been explicitly confirmed to exist. This makes it impossible to expose the
// tab against the not-yet-built GET /training/aiqm/insights/sentiment endpoint by
// flipping only the UI flag (which would error for every user on open).
//
//   VITE_AIQM_INSIGHTS_ENABLED          — show the tab at all
//   VITE_AIQM_INSIGHTS_MOCK             — service serves deterministic mock data
//   VITE_AIQM_INSIGHTS_BACKEND_ENABLED  — set ONLY once the real endpoint exists
const INSIGHTS_UI_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_ENABLED === "true";
const INSIGHTS_MOCK_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
const INSIGHTS_BACKEND_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_BACKEND_ENABLED === "true";
const INSIGHTS_ENABLED =
    INSIGHTS_UI_ENABLED && (INSIGHTS_MOCK_ENABLED || INSIGHTS_BACKEND_ENABLED);

// Call tagging needs backend that persists tags + serves the tags endpoint
// (see BE_HANDOFF_AIQM_CALL_TAGS.md). Until that ships, hide the tag UI so users
// can't enter tags that get silently dropped. Mock mode shows it for demos; set
// VITE_AIQM_TAGS_ENABLED=true only once the real endpoints exist.
const AIQM_TAGS_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true" ||
    import.meta.env.VITE_AIQM_TAGS_ENABLED === "true";

// Inline filename rename needs PATCH /training/aiqm/sessions/:id (see
// BE_HANDOFF_AIQM_CALL_TAGS.md §f). Until that ships, show the filename
// read-only so users can't hit a missing endpoint. Mock mode shows the editor
// for demos; set VITE_AIQM_RENAME_ENABLED=true once the endpoint exists.
const AIQM_RENAME_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true" ||
    import.meta.env.VITE_AIQM_RENAME_ENABLED === "true";

// "Uploaded by" column + filter need backend that returns uploader info,
// serves GET /training/aiqm/uploaders, and honors ?uploaded_by= (see
// BE_HANDOFF_AIQM_CALL_TAGS.md §e). Until that ships, hide the column/filter and
// don't send the param so users can't trigger unsupported requests. Mock mode
// shows it for demos; set VITE_AIQM_UPLOADER_FILTER_ENABLED=true once live.
const AIQM_UPLOADER_FILTER_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true" ||
    import.meta.env.VITE_AIQM_UPLOADER_FILTER_ENABLED === "true";

// "Handled by" (trainee) column + filter + assign actions need backend that
// returns handled_by on the call payload, serves the trainees roster, honors
// ?handled_by=, and exposes the assign endpoints (see BE #3193). Until that
// ships, hide the column/filter and don't send the param so users can't trigger
// unsupported requests. Mock mode shows it for demos; set
// VITE_AIQM_HANDLED_BY_ENABLED=true once the endpoints are live.
const AIQM_HANDLED_BY_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true" ||
    import.meta.env.VITE_AIQM_HANDLED_BY_ENABLED === "true";

type AiqmTab = "calls" | "insights";

// Status chip config mirrors TrainingStatusPill styling used across the platform
const STATUS_CHIP: Record<CallSessionStatus, { label: string; pill: string }> = {
    pending:       { label: "Pending",      pill: "bg-gray-50 text-gray-600 border-gray-200" },
    active:        { label: "In Progress",  pill: "bg-blue-50 text-blue-700 border-blue-200" },
    scheduled:     { label: "Scheduled",    pill: "bg-purple-50 text-purple-700 border-purple-200" },
    analyzing:     { label: "Analyzing",    pill: "bg-amber-50 text-amber-700 border-amber-200" },
    transcribing:  { label: "Transcribing", pill: "bg-amber-50 text-amber-700 border-amber-200" },
    scoring:       { label: "Scoring",      pill: "bg-amber-50 text-amber-700 border-amber-200" },
    results_ready: { label: "Complete",     pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    failed:        { label: "Failed",       pill: "bg-rose-50 text-rose-700 border-rose-200" },
    cancelled:     { label: "Cancelled",    pill: "bg-gray-50 text-gray-600 border-gray-200" },
};

// Stronger solid backgrounds so grade badges stand out against the white table
const GRADE_BADGE: Record<string, { bg: string; text: string }> = {
    A: { bg: "bg-emerald-100", text: "text-emerald-900" },
    B: { bg: "bg-blue-100",    text: "text-blue-900" },
    C: { bg: "bg-amber-100",   text: "text-amber-900" },
    D: { bg: "bg-orange-100",  text: "text-orange-900" },
    F: { bg: "bg-rose-100",    text: "text-rose-900" },
};

// "17 May 2026 at 4.40PM"
function formatDateTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const day = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: "short" });
    const year = date.getFullYear();
    const h = date.getHours();
    const m = String(date.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${day} ${month} ${year} at ${h12}.${m}${ampm}`;
}

function formatDurationMMSS(seconds: number | null | undefined): string {
    if (!seconds || !Number.isFinite(seconds)) return "--:--";
    const total = Math.round(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

const PER_PAGE = 20;
// Sentinel in the tag-filter selection meaning "calls with no tags". Travels
// inside the same string[] as real tags; never sent to the BE as a real tag.
const UNTAGGED_TAG_KEY = "__untagged__";
const MAX_FILES = 100;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB per file
const MAX_TOTAL_UPLOAD_SIZE = 1000 * 1024 * 1024; // 1 GB per batch
// Audio call recordings plus .txt chat transcripts (Live Chat QA). The BE accepts
// both through the same upload endpoint and branches on file extension.
const ACCEPTED_EXTENSIONS = [".mp3", ".wav", ".m4a", ".txt"];
const ACCEPTED_MIME_TYPES = ["audio/mpeg", "audio/wav", "audio/x-wav", "audio/mp4", "audio/x-m4a", "text/plain"];

type FileEntryStatus = "queued" | "uploading" | "error";

interface FileEntry {
    id: string;
    file: File;
    status: FileEntryStatus;
    errorMessage?: string;
}

type SortKey = "filename" | "created_at" | "overall_score" | "overall_grade";
type SortDir = "asc" | "desc";

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFile(file: File): string | null {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_EXTENSIONS.includes(ext) && !ACCEPTED_MIME_TYPES.includes(file.type)) {
        return `Unsupported format. Use ${ACCEPTED_EXTENSIONS.join(", ")}.`;
    }
    if (file.size > MAX_FILE_SIZE) {
        return `File too large (max ${formatFileSize(MAX_FILE_SIZE)}).`;
    }
    return null;
}

// Underline-style tab button matching the platform's section-nav look.
function TabButton({
    label,
    icon: Icon,
    active,
    onClick,
}: {
    label: string;
    icon: LucideIcon;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                // Page-tab style (icon + label): every page tab carries a leading
                // w-3.5 icon, matching the Campaign detail and Reports tab bars.
                "flex items-center gap-1.5 -mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                active
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
            )}
            aria-current={active ? "page" : undefined}
        >
            <Icon className="w-3.5 h-3.5" />
            {label}
        </button>
    );
}

// Thin shell — renders the layout so AiqmUploadProvider is in the tree before
// any hook consumers run. All page logic lives in AiqmPageContent below.
export default function TrainingAiqmPage() {
    return (
        <TrainingAuthLayout>
            <AiqmPageContent />
        </TrainingAuthLayout>
    );
}

function AiqmPageContent() {
    const navigate = useNavigate();
    const [, currentUser] = useUserProfileContext();
    const canWrite = currentUser?.permission !== "member";
    // Superbank uploads their own Bahasa-Indonesia live-chat rubric instead of
    // the generic Customer Service default. Gated by company_id (see
    // @constant/company); works on both staging and production workspaces.
    const isSuperbank = isSuperbankCompany(currentUser?.company_id);
    const defaultRubric = isSuperbank ? SUPERBANK_LIVECHAT_RUBRIC : DEFAULT_RUBRIC;
    // Trial/usage gate for uploading AIQM call audio (training-agent workspaces).
    const { isTrainingAiqmBlocked } = useSubscription();
    const aiqmBlockedTitle = isTrainingAiqmBlocked
        ? "Your trial has expired or your AIQM upload limit is reached. Upgrade to upload audio."
        : undefined;
    const [searchParams, setSearchParams] = useSearchParams();
    // Active tab, persisted in the URL (?tab=insights) so a manager can share a
    // direct link. Falls back to "calls" and ignores "insights" when the flag is off.
    const tabParam = searchParams.get("tab");
    const activeTab: AiqmTab =
        INSIGHTS_ENABLED && tabParam === "insights" ? "insights" : "calls";
    const [data, setData] = useState<CallSessionListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [search, setSearch] = useState(searchParams.get("search") || "");
    // "Uploaded by" filter — persisted in the URL so it survives navigation.
    const uploaderFilter = searchParams.get("uploaded_by") || "";
    const [uploaders, setUploaders] = useState<AiqmUploader[]>([]);
    // Tracks whether the uploader list actually loaded. A failed fetch (broken
    // route / auth) must hide the filter rather than render a misleading
    // "All uploaders"-only dropdown that looks like an empty dataset.
    const [uploadersLoaded, setUploadersLoaded] = useState(false);

    // "Handled by" (trainee) filter — server-side like uploaded_by, persisted in
    // the URL so it survives navigation.
    const handledByFilter = searchParams.get("handled_by") || "";
    const [trainees, setTrainees] = useState<AiqmTrainee[]>([]);
    // Same failed-fetch guard as the uploader list: hide the filter rather than
    // show an empty, misleading "All trainees"-only dropdown.
    const [traineesLoaded, setTraineesLoaded] = useState(false);

    // Tag multi-select filter — client-side over the loaded page (same precedent
    // as the Grade filter) so per-tag counts and hide-zero are truthful. Empty =
    // no tag filter (all calls). OR-within-tags.
    const [tagFilter, setTagFilter] = useState<string[]>([]);

    // Row selection for bulk assign. Holds session ids of checked rows; the
    // floating action bar appears while non-empty (write-gated).
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Upload dialog state (local to this page — the context handles tracking after upload)
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [uploadStep, setUploadStep] = useState<1 | 2 | 3>(1);
    const [files, setFiles] = useState<FileEntry[]>([]);
    const [rubricCriteria, setRubricCriteria] = useState<RubricCriterion[]>(DEFAULT_RUBRIC);
    // The profile (and thus company_id) hydrates async, after the initial
    // DEFAULT_RUBRIC seed above. Once we know the user is Superbank, swap in their
    // rubric — one-shot so it never clobbers edits the user makes afterwards.
    const superbankSeededRef = useRef(false);
    useEffect(() => {
        if (superbankSeededRef.current) return;
        if (isSuperbank) {
            setRubricCriteria(SUPERBANK_LIVECHAT_RUBRIC);
            superbankSeededRef.current = true;
        }
    }, [isSuperbank]);
    // Tags applied to this upload batch — group calls for the Insights tag filter.
    const [uploadTags, setUploadTags] = useState<string[]>([]);
    const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Step 3 (flag-gated) — upload-scoped knowledge. Workspace docs apply BY
    // DEFAULT and render as included rows the user can remove per-batch; new
    // PDF files influence only this upload. See KB Phase 2 handoff.
    const uploadTotalSteps = KB_AIQM_BATCH_KNOWLEDGE_ENABLED ? 3 : 2;
    const [knowledgeFiles, setKnowledgeFiles] = useState<File[]>([]);
    const [excludedKnowledgeIds, setExcludedKnowledgeIds] = useState<Set<string>>(new Set());
    const [workspaceKnowledgeDocs, setWorkspaceKnowledgeDocs] = useState<KbDocument[]>([]);
    const [knowledgeDragging, setKnowledgeDragging] = useState(false);
    const knowledgeFileInputRef = useRef<HTMLInputElement>(null);

    // Load the workspace KB list when the dialog opens so the Knowledge step
    // reflects docs uploaded moments ago.
    useEffect(() => {
        if (!KB_AIQM_BATCH_KNOWLEDGE_ENABLED || !isUploadOpen) return;
        let cancelled = false;
        // Scope to workspace: the batch inherits workspace docs by default. An
        // unscoped list would surface other campaigns' scoped docs as inherited
        // once the Phase 1 backend returns campaign attachments here.
        listAllKbDocuments({ scope: "workspace" })
            .then((docs) => {
                if (!cancelled) setWorkspaceKnowledgeDocs(docs);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isUploadOpen]);

    function addKnowledgeFiles(fileList: FileList | File[]) {
        // PDF only (product decision 2026-07-16).
        const valid = Array.from(fileList).filter(
            (f) => `.${f.name.split(".").pop()?.toLowerCase()}` === ".pdf",
        );
        setKnowledgeFiles((prev) => [...prev, ...valid]);
    }

    function toggleKnowledgeExclusion(id: string) {
        setExcludedKnowledgeIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    // Table sort state — default newest first
    const [sortKey, setSortKey] = useState<SortKey>("created_at");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [gradeFilter, setGradeFilter] = useState("all");

    const { uploadState, isProcessing, beginUpload, attachSessions, applyWsProgress, markUploadFailed } = useAiqmUpload();

    // Real-time per-file progress over the BE WebSocket (#2517). Routes each
    // `progress` event into the context (which matches it by batch_id / session_id).
    // The WS is the sole progress source — there is no polling fallback (pure-WS,
    // same as bulk-CV). Disconnected when the batch finishes / on unmount.
    const { connect: connectUploadWs, disconnect: disconnectUploadWs } = useAiqmUploadProgress(
        currentUser?.id ?? "",
        import.meta.env.VITE_TALENT_AI_BE_BASE_URL || "",
        applyWsProgress,
        // On a reload the socket reconnects with this batch id so the server
        // replays the batch's latest per-file snapshot (#3025).
        uploadState?.batchId ?? null,
    );

    // Drive the socket lifecycle off the active upload, connecting ONCE per upload
    // lifecycle. We must NOT reconnect on the `null → batchId` transition: this
    // hook's connect() closes+reopens the socket, and that gap could drop the
    // first per-file WS events — with no polling fallback, a missed terminal event
    // leaves a row stuck in "analyzing" forever. A completed/failed batch flips
    // `isTrackingUpload` false (resetting the ref), so a RETRY reconnects fresh;
    // and the socket is user-scoped, so every batch's events arrive on it and are
    // correlated by `session_id` (no per-batch reconnect needed).
    const isTrackingUpload =
        uploadState?.phase === "uploading" || uploadState?.phase === "analyzing";
    const hasConnectedForActiveUploadRef = useRef(false);
    useEffect(() => {
        if (!isTrackingUpload) {
            hasConnectedForActiveUploadRef.current = false;
            disconnectUploadWs();
            return;
        }
        // Wait for a real user id before claiming the one-shot connect: the hook's
        // connect() no-ops on an empty id, so if an upload is already in
        // "uploading"/"analyzing" before `currentUser` hydrates, latching the ref
        // here would consume the only connect window and leave the batch stuck.
        if (!currentUser?.id) return;
        if (!hasConnectedForActiveUploadRef.current) {
            connectUploadWs();
            hasConnectedForActiveUploadRef.current = true;
        }
    }, [isTrackingUpload, currentUser?.id, connectUploadWs, disconnectUploadWs]);

    // Always tear the socket down on unmount.
    useEffect(() => () => disconnectUploadWs(), [disconnectUploadWs]);

    const page = Number(searchParams.get("page") || "1");

    // `silent` skips the loading skeleton / error-state swap so a background
    // refresh (e.g. the incremental reload as each upload succeeds) updates the
    // rows in place instead of flashing the whole table.
    const fetchSessions = useCallback((opts?: { silent?: boolean }) => {
        const silent = opts?.silent ?? false;
        if (!silent) setLoading(true);
        setError(null);
        getCallSessions({
            page,
            per_page: PER_PAGE,
            status: "results_ready",
            search: search || undefined,
            uploaded_by:
                AIQM_UPLOADER_FILTER_ENABLED && uploaderFilter
                    ? uploaderFilter
                    : undefined,
            handled_by:
                AIQM_HANDLED_BY_ENABLED && handledByFilter
                    ? handledByFilter
                    : undefined,
        })
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch((err: unknown) => {
                const e = err instanceof Error ? err : new Error(String(err));
                const is404 =
                    typeof err === "object" &&
                    err !== null &&
                    "response" in err &&
                    (err as { response?: { status?: number } }).response?.status === 404;
                if (is404) {
                    // Keep existing rows on a silent refresh; only a foreground
                    // load resets the table to the empty state.
                    if (!silent) setData({ sessions: [], total: 0, page: 1, per_page: PER_PAGE });
                    setLoading(false);
                    return;
                }
                catchErrorLog(`[aiqm] sessions load failed: ${e.message}`);
                // A silent background refresh keeps the current rows rather than
                // replacing the table with an error state.
                if (!silent) setError(e);
                setLoading(false);
            });
    }, [page, search, uploaderFilter, handledByFilter]);

    useEffect(() => {
        // Don't pay for the calls-list request on a deep link to ?tab=insights —
        // that view only needs the sentiment aggregate. The list loads when the
        // user is (or switches) to the Calls tab.
        if (activeTab !== "calls") return;
        fetchSessions();
    }, [activeTab, fetchSessions]);

    const fetchSessionsRef = useRef(fetchSessions);
    useEffect(() => { fetchSessionsRef.current = fetchSessions; }, [fetchSessions]);

    // Number of files that have SUCCEEDED so far in the active upload. Each new
    // success means a fresh AIQM session is queryable on the server.
    const uploadSucceededCount = (uploadState?.sessions ?? []).filter(
        (s) => s.serverStatus === "results_ready",
    ).length;

    // Incrementally reload the Calls table as soon as EACH file succeeds — don't
    // wait for the whole batch to complete — so a finished recording appears in
    // the list right away. The refresh is silent (no skeleton flash), and we
    // also refresh once the batch is done (final reconcile, e.g. if the last
    // file failed and didn't bump the succeeded count).
    useEffect(() => {
        if (activeTab !== "calls") return;
        if (uploadSucceededCount > 0 || uploadState?.phase === "done") {
            fetchSessionsRef.current({ silent: true });
        }
    }, [uploadSucceededCount, uploadState?.phase, activeTab]);

    function updateParams(updates: Record<string, string>) {
        const next = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(updates)) {
            if (v) next.set(k, v);
            else next.delete(k);
        }
        setSearchParams(next);
    }

    // Switch tabs via the URL. "calls" clears the param (it's the default) so the
    // canonical list URL stays clean.
    function setActiveTab(tab: AiqmTab) {
        updateParams({ tab: tab === "insights" ? "insights" : "" });
    }

    function addFiles(incoming: FileList | File[]) {
        const newFiles: FileEntry[] = [];
        const fileArray = Array.from(incoming);

        for (const file of fileArray) {
            if (files.length + newFiles.length >= MAX_FILES) break;

            const validationError = validateFile(file);
            newFiles.push({
                id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                file,
                status: validationError ? "error" : "queued",
                errorMessage: validationError || undefined,
            });
        }

        setFiles((prev) => [...prev, ...newFiles].slice(0, MAX_FILES));
    }

    function removeFile(id: string) {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    }

    function clearFiles() {
        if (isUploading) return;
        setFiles([]);
    }

    function resetUploadDialog() {
        if (isUploading) return;
        setFiles([]);
        setRubricCriteria(defaultRubric);
        setUploadTags([]);
        setKnowledgeFiles([]);
        setExcludedKnowledgeIds(new Set());
        setUploadStep(1);
    }

    // Load existing tags for autocomplete — used by both the upload dialog and
    // the inline tag column. Fetched once on mount when tagging is enabled.
    useEffect(() => {
        if (!AIQM_TAGS_ENABLED) return;
        let active = true;
        listAiqmTags()
            .then((tags) => {
                if (active) setTagSuggestions(tags);
            })
            .catch(() => {
                /* suggestions are optional */
            });
        return () => {
            active = false;
        };
    }, []);

    // Load the list of uploaders for the "Uploaded by" filter (once on mount).
    useEffect(() => {
        if (!AIQM_UPLOADER_FILTER_ENABLED) return;
        let active = true;
        listAiqmUploaders()
            .then((list) => {
                if (!active) return;
                setUploaders(list);
                setUploadersLoaded(true);
            })
            .catch(() => {
                // Route/auth failure — keep the page working but hide the filter
                // (handled via showUploaderFilter) instead of showing it empty.
                if (!active) return;
                setUploaders([]);
                setUploadersLoaded(false);
            });
        return () => {
            active = false;
        };
    }, []);

    // The filter only renders once the uploader list has loaded successfully.
    const showUploaderFilter = AIQM_UPLOADER_FILTER_ENABLED && uploadersLoaded;

    // Load the trainee roster for the "Handled by" filter + assign menu (once on
    // mount). Same failed-fetch guard as the uploader list.
    useEffect(() => {
        if (!AIQM_HANDLED_BY_ENABLED) return;
        let active = true;
        listAiqmTrainees()
            .then((list) => {
                if (!active) return;
                setTrainees(list);
                setTraineesLoaded(true);
            })
            .catch(() => {
                if (!active) return;
                setTrainees([]);
                setTraineesLoaded(false);
            });
        return () => {
            active = false;
        };
    }, []);

    // The handled-by filter only renders once the roster has loaded successfully.
    const showHandledByFilter = AIQM_HANDLED_BY_ENABLED && traineesLoaded;

    // Drop selected ids that are no longer on the loaded page (after a refetch,
    // page change, or filter change) so the bulk bar never acts on stale rows.
    useEffect(() => {
        const visible = new Set((data?.sessions ?? []).map((s) => s.id));
        setSelectedIds((prev) => {
            const next = new Set<string>();
            Array.from(prev).forEach((id) => {
                if (visible.has(id)) next.add(id);
            });
            return next.size === prev.size ? prev : next;
        });
    }, [data?.sessions]);

    const rubricWeightSum = rubricCriteria.reduce((sum, c) => sum + c.weight, 0);
    const isRubricValid = rubricWeightSum === 100;
    const queuedFiles = files.filter((f) => f.status === "queued");
    const queuedCount = queuedFiles.length;
    const hasQueuedFile = queuedCount > 0;
    const totalQueuedSize = queuedFiles.reduce((sum, f) => sum + f.file.size, 0);
    const isBatchTooLarge = totalQueuedSize > MAX_TOTAL_UPLOAD_SIZE;
    const canAdvanceToRubric = hasQueuedFile && !isUploading && !isBatchTooLarge;

    async function handleUpload() {
        const queued = files.filter((f) => f.status === "queued");
        if (queued.length === 0) return;
        if (!isRubricValid) return;
        const totalSize = queued.reduce((sum, f) => sum + f.file.size, 0);
        if (totalSize > MAX_TOTAL_UPLOAD_SIZE) return;

        // Capture before resetUploadDialog() clears the dialog state below.
        const submittedRubric = rubricCriteria;
        const submittedTags = AIQM_TAGS_ENABLED ? uploadTags : [];
        const submittedKnowledgeFiles = KB_AIQM_BATCH_KNOWLEDGE_ENABLED ? knowledgeFiles : [];
        const submittedExcludedKnowledgeIds = KB_AIQM_BATCH_KNOWLEDGE_ENABLED
            ? Array.from(excludedKnowledgeIds)
            : [];
        // Hand off to context toast immediately so it shows across navigation
        beginUpload(queued.map((f) => f.file.name));
        setIsUploadOpen(false);
        resetUploadDialog();

        setIsUploading(true);
        // The socket is opened by the `isTrackingUpload` effect as soon as
        // beginUpload() flips the phase to "uploading" (which already happened
        // above), so we don't connect here — that keeps a single lifecycle owner
        // and lets retries re-open tracking through the same path. Any progress
        // event that races ahead of attachSessions() is buffered in the context
        // and replayed once the batchId is set, so nothing is dropped.

        try {
            const { sessions: uploads, batchId } = await uploadAudioFiles(
                queued.map((f) => f.file),
                {
                    rubricCriteria: submittedRubric,
                    tags: submittedTags,
                    ...(submittedKnowledgeFiles.length > 0
                        ? { knowledgeFiles: submittedKnowledgeFiles }
                        : {}),
                    ...(submittedExcludedKnowledgeIds.length > 0
                        ? { excludedKnowledgeDocumentIds: submittedExcludedKnowledgeIds }
                        : {}),
                },
            );

            // Per-file progress is correlated by `session_id` ALONE (see
            // applyProgressToState) — the WS is user-scoped and session ids are
            // globally unique, so a missing/blank `batch_id` does NOT prevent
            // tracking. Only bail when the POST returned no sessions at all
            // (nothing for the WS events to resolve against). Hard-failing on a
            // missing batch_id was wrong: some BE responses omit it, which then
            // dropped a fully-processed upload into "System Error" even though the
            // WS streamed a `completed`/`results_ready` for it.
            if (uploads.length === 0) {
                throw new Error(
                    "Upload succeeded but the server returned no sessions to track. Please try again.",
                );
            }

            // Per-file terminal events stream in over the WS, matched by
            // `session_id` (batchId is informational and may be null).
            attachSessions(
                uploads.map((u) => ({
                    sessionId: u.session_id,
                    filename: u.filename,
                    status: u.status,
                })),
                batchId,
            );
        } catch (err: unknown) {
            // Upload failed → there's no batch to track. markUploadFailed() flips
            // the phase out of uploading/analyzing, which makes the tracking
            // effect disconnect; tear the socket down here too so it stops
            // reconnecting immediately rather than waiting for the next render.
            disconnectUploadWs();
            const msg = err instanceof Error ? err.message : "Upload failed";
            catchErrorLog(`[aiqm] upload failed: ${msg}`);
            markUploadFailed(msg);
        } finally {
            setIsUploading(false);
        }
    }

    function handleDragOver(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(true);
    }

    function handleDragLeave(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
        }
    }

    function toggleSort(key: SortKey) {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("desc");
        }
    }

    // Toggle a single row's selection for bulk assign.
    function toggleRowSelected(id: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    // Bulk-assign one trainee (or unassign with null) across the selected rows.
    // Optimistically patches handled_by on the affected rows in the loaded list,
    // then clears the selection. Rolls back the list on failure.
    const [bulkAssigning, setBulkAssigning] = useState(false);
    async function bulkAssign(traineeId: string | null) {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        const idSet = new Set(ids);
        const trainee = traineeId
            ? (trainees.find((t) => t.id === traineeId) ?? { id: traineeId, name: traineeId })
            : null;
        const prev = data;
        // Optimistic update on the loaded list.
        setData((d) =>
            d
                ? {
                      ...d,
                      sessions: d.sessions.map((s) =>
                          idSet.has(s.id) ? { ...s, handled_by: trainee } : s,
                      ),
                  }
                : d,
        );
        setBulkAssigning(true);
        try {
            await bulkAssignCallHandler(ids, traineeId);
            setSelectedIds(new Set());
            // Reload from the server so the table reflects the persisted handler
            // (the optimistic patch above is only a local guess).
            fetchSessions();
        } catch (err) {
            setData(prev); // rollback
            catchErrorLog(
                `bulkAssignCallHandler failed for ${ids.length} calls: ${
                    err instanceof Error ? err.message : String(err)
                }`,
            );
        } finally {
            setBulkAssigning(false);
        }
    }

    // Bulk-add tags across the selected rows (ADD/merge — unions the chosen tags
    // into each row's existing tags, never removes). Optimistically merges on the
    // loaded list, then clears the selection. Rolls back on failure.
    const [bulkTagging, setBulkTagging] = useState(false);
    async function bulkAddTags(tags: string[]) {
        const ids = Array.from(selectedIds);
        if (ids.length === 0 || tags.length === 0) return;
        const idSet = new Set(ids);
        const prev = data;
        // Optimistic union merge on the loaded list.
        setData((d) =>
            d
                ? {
                      ...d,
                      sessions: d.sessions.map((s) =>
                          idSet.has(s.id)
                              ? {
                                    ...s,
                                    tags: Array.from(
                                        new Set([...(s.tags ?? []), ...tags]),
                                    ),
                                }
                              : s,
                      ),
                  }
                : d,
        );
        setBulkTagging(true);
        try {
            await bulkAssignTags(ids, tags);
            setSelectedIds(new Set());
            // Reload from the server so the table reflects the persisted tags
            // (the optimistic merge above is only a local guess).
            fetchSessions();
        } catch (err) {
            setData(prev); // rollback
            catchErrorLog(
                `bulkAssignTags failed for ${ids.length} calls: ${
                    err instanceof Error ? err.message : String(err)
                }`,
            );
        } finally {
            setBulkTagging(false);
        }
    }

    // Patch a single row's handled_by in the loaded list (used by the inline
    // assign cell so its optimistic state stays in sync with the table data).
    function patchRowHandledBy(sessionId: string, handledBy: AiqmTrainee | null) {
        setData((d) =>
            d
                ? {
                      ...d,
                      sessions: d.sessions.map((s) =>
                          s.id === sessionId ? { ...s, handled_by: handledBy } : s,
                      ),
                  }
                : d,
        );
    }

    const totalPages = data ? Math.ceil(data.total / PER_PAGE) : 0;
    const hasFiles = files.length > 0;

    const sorted = useMemo(() => {
        const visibleSessions = data?.sessions ?? [];
        const gradeOrder: Record<string, number> = { A: 1, B: 2, C: 3, D: 4, F: 5 };
        return [...visibleSessions].sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case "filename": {
                    const an = (a.filename || a.contact_name || "").toLowerCase();
                    const bn = (b.filename || b.contact_name || "").toLowerCase();
                    cmp = an.localeCompare(bn);
                    break;
                }
                case "created_at":
                    cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    break;
                case "overall_score":
                    cmp = (a.overall_score ?? -1) - (b.overall_score ?? -1);
                    break;
                case "overall_grade": {
                    const ag = a.overall_grade ? (gradeOrder[a.overall_grade] ?? 99) : 99;
                    const bg = b.overall_grade ? (gradeOrder[b.overall_grade] ?? 99) : 99;
                    cmp = ag - bg;
                    break;
                }
            }
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [data?.sessions, sortKey, sortDir]);

    const gradeCounts = useMemo(() => {
        const counts: Record<string, number> = { all: data?.sessions.length ?? 0 };
        for (const s of data?.sessions ?? []) {
            if (s.overall_grade) {
                counts[s.overall_grade] = (counts[s.overall_grade] ?? 0) + 1;
            }
        }
        return counts;
    }, [data?.sessions]);

    // Per-tag counts over the loaded page (scoped like gradeCounts). `all` is the
    // total session count; `__untagged__` counts calls with no tags. Powers the
    // "All tags (count)" header + per-option counts + hide-zero in the dropdown.
    const tagCounts = useMemo(() => {
        const counts: Record<string, number> = { all: data?.sessions.length ?? 0 };
        let untagged = 0;
        for (const s of data?.sessions ?? []) {
            const tags = s.tags ?? [];
            if (tags.length === 0) untagged += 1;
            for (const t of tags) counts[t] = (counts[t] ?? 0) + 1;
        }
        counts[UNTAGGED_TAG_KEY] = untagged;
        return counts;
    }, [data?.sessions]);

    // Distinct tag options present on the loaded page, sorted alphabetically.
    const tagOptions = useMemo(() => {
        const set = new Set<string>();
        for (const s of data?.sessions ?? []) {
            for (const t of s.tags ?? []) set.add(t);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [data?.sessions]);

    const gradeFiltered = gradeFilter === "all"
        ? sorted
        : sorted.filter((s) => s.overall_grade === gradeFilter);

    // Tag filter — client-side OR-within-tags over the grade-filtered rows (same
    // precedent as the Grade filter). The UNTAGGED sentinel matches calls with no
    // tags. Empty selection = no tag filter.
    const tagFiltered = useMemo(() => {
        if (!AIQM_TAGS_ENABLED || tagFilter.length === 0) return gradeFiltered;
        const wantUntagged = tagFilter.includes(UNTAGGED_TAG_KEY);
        const wantTags = tagFilter.filter((t) => t !== UNTAGGED_TAG_KEY);
        return gradeFiltered.filter((s) => {
            const tags = s.tags ?? [];
            if (wantUntagged && tags.length === 0) return true;
            return wantTags.some((t) => tags.includes(t));
        });
    }, [gradeFiltered, tagFilter]);

    const visibleRows = tagFiltered;

    // Reset the tag filter whenever the page/search/server filters change so it
    // never references tags that aren't on the freshly-loaded page.
    useEffect(() => {
        setTagFilter([]);
    }, [page, search, uploaderFilter, handledByFilter]);

    // Bulk-assign affordances only for writers. Each bulk action is gated by its
    // own feature flag; row selection shows when at least one is available.
    const showBulkAssign = AIQM_HANDLED_BY_ENABLED && canWrite;
    const showBulkTags = AIQM_TAGS_ENABLED && canWrite;
    const showBulkSelect = showBulkAssign || showBulkTags;
    const allRowsSelected =
        visibleRows.length > 0 && visibleRows.every((s) => selectedIds.has(s.id));
    const someRowsSelected = visibleRows.some((s) => selectedIds.has(s.id));
    const selectedCount = selectedIds.size;

    // --- Consolidated filters (single "Filters" popover + active-filter chips) ---
    function toggleTag(key: string) {
        setTagFilter((prev) =>
            prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key],
        );
    }
    function clearAllFilters() {
        setGradeFilter("all");
        setTagFilter([]);
        updateParams({ uploaded_by: "", handled_by: "", page: "1" });
    }
    // One removable chip per applied filter (each selected tag is its own chip).
    const activeFilterChips = useMemo(() => {
        const chips: { key: string; label: string; onRemove: () => void }[] = [];
        if (gradeFilter !== "all") {
            chips.push({
                key: "grade",
                label: `Grade ${gradeFilter}`,
                onRemove: () => setGradeFilter("all"),
            });
        }
        if (showUploaderFilter && uploaderFilter) {
            const name =
                uploaders.find((u) => u.id === uploaderFilter)?.name ?? uploaderFilter;
            chips.push({
                key: "uploader",
                label: `Uploaded by: ${name}`,
                onRemove: () => updateParams({ uploaded_by: "", page: "1" }),
            });
        }
        if (showHandledByFilter && handledByFilter) {
            const name =
                trainees.find((t) => t.id === handledByFilter)?.name ?? handledByFilter;
            chips.push({
                key: "handler",
                label: `Handled by: ${name}`,
                onRemove: () => updateParams({ handled_by: "", page: "1" }),
            });
        }
        if (AIQM_TAGS_ENABLED) {
            for (const t of tagFilter) {
                chips.push({
                    key: `tag:${t}`,
                    label: t === UNTAGGED_TAG_KEY ? "Untagged" : t,
                    onRemove: () =>
                        setTagFilter((prev) => prev.filter((x) => x !== t)),
                });
            }
        }
        return chips;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        gradeFilter,
        uploaderFilter,
        handledByFilter,
        tagFilter,
        uploaders,
        trainees,
        showUploaderFilter,
        showHandledByFilter,
    ]);

    return (
        <>
        <div>
                {/* Page header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            AI Quality Monitoring
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Upload and analyze calls and chat logs for quality assessment.
                        </p>
                    </div>
                    {canWrite && (
                        <Button
                            onClick={() => setIsUploadOpen(true)}
                            disabled={isTrainingAiqmBlocked || isProcessing}
                            title={
                                isProcessing
                                    ? "An upload is in progress. Please wait until it finishes before starting another."
                                    : aiqmBlockedTitle
                            }
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2Icon className="w-4 h-4 animate-spin mr-1.5" />
                                    {uploadState?.phase === "uploading" ? "Uploading..." : "Analyzing..."}
                                </>
                            ) : (
                                <>
                                    <UploadIcon className="w-4 h-4" />
                                    Upload Files
                                </>
                            )}
                        </Button>
                    )}
                </div>

                {/* Tab nav — only shown when the Insights feature flag is on */}
                {INSIGHTS_ENABLED && (
                    <div className="mb-5 flex items-center gap-1 border-b border-gray-200">
                        <TabButton
                            label="Calls"
                            icon={PhoneCallIcon}
                            active={activeTab === "calls"}
                            onClick={() => setActiveTab("calls")}
                        />
                        <TabButton
                            label="Insights"
                            icon={BarChart3Icon}
                            active={activeTab === "insights"}
                            onClick={() => setActiveTab("insights")}
                        />
                    </div>
                )}

                {activeTab === "insights" ? (
                    <AiqmSentimentInsights />
                ) : (
                <>
                {/* Search + consolidated filters */}
                <div className="mb-4 space-y-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Search by contact name or phone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        updateParams({ search, page: "1" });
                                    }
                                }}
                                className="pl-9"
                            />
                        </div>
                        {/* Single Filters entry point — every filter dimension lives
                            in one popover; applied filters surface as chips below.
                            Inline controls (not nested Selects) so picking an option
                            doesn't dismiss the popover. */}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="h-9 flex-shrink-0 gap-2">
                                    <SlidersHorizontalIcon className="h-4 w-4" />
                                    Filters
                                    {activeFilterChips.length > 0 && (
                                        <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-100 px-1.5 text-[10px] font-semibold tabular-nums text-blue-700">
                                            {activeFilterChips.length}
                                        </span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-72 p-0">
                                <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2.5">
                                    <p className="text-sm font-semibold text-gray-900">Filters</p>
                                    {activeFilterChips.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={clearAllFilters}
                                            className="text-xs font-medium text-blue-600 hover:underline"
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                <div className="max-h-[60vh] space-y-4 overflow-y-auto p-3">
                                    {/* Grade */}
                                    <div>
                                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                            Grade
                                        </p>
                                        <div className="flex flex-wrap items-center gap-1">
                                            <FilterPill
                                                active={gradeFilter === "all"}
                                                onClick={() => setGradeFilter("all")}
                                                label="All"
                                                count={gradeCounts.all ?? 0}
                                            />
                                            {(["A", "B", "C", "D", "F"] as const).map((g) => {
                                                const count = gradeCounts[g] ?? 0;
                                                if (count === 0) return null;
                                                return (
                                                    <FilterPill
                                                        key={g}
                                                        active={gradeFilter === g}
                                                        onClick={() => setGradeFilter(g)}
                                                        label={g}
                                                        count={count}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {/* Uploaded by */}
                                    {showUploaderFilter && (
                                        <div>
                                            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                Uploaded by
                                            </p>
                                            <div className="max-h-40 overflow-y-auto">
                                                <FilterRadioRow
                                                    selected={!uploaderFilter}
                                                    onClick={() =>
                                                        updateParams({ uploaded_by: "", page: "1" })
                                                    }
                                                    label="All uploaders"
                                                />
                                                {uploaders.map((u) => (
                                                    <FilterRadioRow
                                                        key={u.id}
                                                        selected={uploaderFilter === u.id}
                                                        onClick={() =>
                                                            updateParams({ uploaded_by: u.id, page: "1" })
                                                        }
                                                        label={u.name}
                                                        count={u.count ?? undefined}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* Handled by */}
                                    {showHandledByFilter && (
                                        <div>
                                            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                Handled by
                                            </p>
                                            <div className="max-h-40 overflow-y-auto">
                                                <FilterRadioRow
                                                    selected={!handledByFilter}
                                                    onClick={() =>
                                                        updateParams({ handled_by: "", page: "1" })
                                                    }
                                                    label="All handlers"
                                                />
                                                {trainees.map((t) => (
                                                    <FilterRadioRow
                                                        key={t.id}
                                                        selected={handledByFilter === t.id}
                                                        onClick={() =>
                                                            updateParams({ handled_by: t.id, page: "1" })
                                                        }
                                                        label={t.name}
                                                        count={t.count ?? undefined}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* Tags */}
                                    {AIQM_TAGS_ENABLED && tagOptions.length > 0 && (
                                        <div>
                                            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                Tags
                                            </p>
                                            <div className="max-h-40 overflow-y-auto">
                                                {(tagCounts[UNTAGGED_TAG_KEY] ?? 0) > 0 && (
                                                    <TagFilterOption
                                                        checked={tagFilter.includes(UNTAGGED_TAG_KEY)}
                                                        onToggle={() => toggleTag(UNTAGGED_TAG_KEY)}
                                                        label="Untagged"
                                                        count={tagCounts[UNTAGGED_TAG_KEY] ?? 0}
                                                        italic
                                                    />
                                                )}
                                                {tagOptions
                                                    .filter((t) => (tagCounts[t] ?? 0) > 0)
                                                    .map((t) => (
                                                        <TagFilterOption
                                                            key={t}
                                                            checked={tagFilter.includes(t)}
                                                            onToggle={() => toggleTag(t)}
                                                            label={t}
                                                            count={tagCounts[t] ?? 0}
                                                        />
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                    {/* Active filter chips */}
                    {activeFilterChips.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            {activeFilterChips.map((chip) => (
                                <span
                                    key={chip.key}
                                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 py-1 pl-2.5 pr-1.5 text-xs font-medium text-gray-700"
                                >
                                    {chip.label}
                                    <button
                                        type="button"
                                        onClick={chip.onRemove}
                                        className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                                        aria-label={`Remove ${chip.label} filter`}
                                    >
                                        <XIcon className="h-3 w-3" />
                                    </button>
                                </span>
                            ))}
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* Sessions table */}
                {error ? (
                    <TrainingErrorCard
                        title="Could not load call sessions."
                        message="The error has been reported. Try again or refresh."
                        onRetry={() => fetchSessions()}
                    />
                ) : loading || !data ? (
                    <Card>
                        <div className="p-5">
                            <SkeletonTable />
                        </div>
                    </Card>
                ) : sorted.length === 0 && gradeFilter === "all" && tagFilter.length === 0 ? (
                    <Card>
                        <div className="p-12 text-center">
                            <PhoneCallIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <h3 className="text-sm font-medium text-gray-900 mb-1">
                                No call sessions yet.
                            </h3>
                            <p className="text-xs text-gray-500 max-w-sm mx-auto">
                                Upload calls or chat logs to start monitoring quality with AI analysis.
                            </p>
                            <Button
                                className="mt-4"
                                onClick={() => setIsUploadOpen(true)}
                                disabled={isTrainingAiqmBlocked || isProcessing}
                                title={
                                    isProcessing
                                        ? "An upload is in progress. Please wait until it finishes before starting another."
                                        : aiqmBlockedTitle
                                }
                            >
                                <UploadIcon className="w-4 h-4" />
                                Upload Files
                            </Button>
                        </div>
                    </Card>
                ) : visibleRows.length === 0 ? (
                    <Card>
                        <div className="px-6 py-10 text-center text-sm text-gray-500 italic">
                            No sessions match the selected filters.
                        </div>
                    </Card>
                ) : (
                    <>
                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            {/* Bulk-select checkbox header — only for writers and only
                                                when at least one bulk action (handled-by / tags) is available. */}
                                            {showBulkSelect && (
                                                <th className="h-[45px] w-px px-4 py-3 text-center">
                                                    <input
                                                        type="checkbox"
                                                        aria-label="Select all calls on this page"
                                                        checked={allRowsSelected}
                                                        ref={(el) => {
                                                            if (el) el.indeterminate = someRowsSelected && !allRowsSelected;
                                                        }}
                                                        onChange={() => {
                                                            if (allRowsSelected) setSelectedIds(new Set());
                                                            else setSelectedIds(new Set(visibleRows.map((s) => s.id)));
                                                        }}
                                                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                </th>
                                            )}
                                            <SortableTh
                                                label="File Name"
                                                sortKey="filename"
                                                current={sortKey}
                                                dir={sortDir}
                                                onSort={toggleSort}
                                                align="left"
                                                widthClass="w-[340px]"
                                            />
                                            <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Summary
                                            </th>
                                            <SortableTh
                                                label="Score"
                                                sortKey="overall_score"
                                                current={sortKey}
                                                dir={sortDir}
                                                onSort={toggleSort}
                                                align="center"
                                                widthClass="w-px"
                                            />
                                            <SortableTh
                                                label="Grade"
                                                sortKey="overall_grade"
                                                current={sortKey}
                                                dir={sortDir}
                                                onSort={toggleSort}
                                                align="center"
                                                widthClass="w-px"
                                            />
                                            <SortableTh
                                                label="Analyzed"
                                                sortKey="created_at"
                                                current={sortKey}
                                                dir={sortDir}
                                                onSort={toggleSort}
                                                align="center"
                                                widthClass="w-px"
                                            />
                                            {AIQM_UPLOADER_FILTER_ENABLED && (
                                                <th className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                    Uploaded by
                                                </th>
                                            )}
                                            {AIQM_HANDLED_BY_ENABLED && (
                                                <th className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                    Handled by
                                                </th>
                                            )}
                                            {AIQM_TAGS_ENABLED && (
                                                <th className="h-[45px] px-4 py-3 w-[180px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                    Tags
                                                </th>
                                            )}
                                            <th className="h-[45px] px-4 py-3 w-[110px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {visibleRows.map((session) => (
                                            <SessionTableRow
                                                key={session.id}
                                                session={session}
                                                tagsEnabled={AIQM_TAGS_ENABLED}
                                                canRename={AIQM_RENAME_ENABLED}
                                                uploaderEnabled={AIQM_UPLOADER_FILTER_ENABLED}
                                                handledByEnabled={AIQM_HANDLED_BY_ENABLED}
                                                canWrite={canWrite}
                                                allTags={tagSuggestions}
                                                trainees={trainees}
                                                selectable={showBulkSelect}
                                                selected={selectedIds.has(session.id)}
                                                onToggleSelected={() => toggleRowSelected(session.id)}
                                                onHandledByChange={(hb) => patchRowHandledBy(session.id, hb)}
                                                onClick={() => navigate(`/training/aiqm/${session.id}`)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            total={data.total}
                            pageSize={PER_PAGE}
                            itemLabel="session"
                            onPageChange={(p) => updateParams({ page: String(p) })}
                            className="mt-4"
                        />
                    </>
                )}
                </>
                )}
            </div>

            {/* Bulk-assign floating action bar — only on the Calls tab, for
                writers, when rows are selected. */}
            {activeTab === "calls" && showBulkSelect && selectedCount > 0 && (
                <BulkAssignBar
                    count={selectedCount}
                    showHandler={showBulkAssign}
                    showTags={showBulkTags}
                    trainees={trainees}
                    allTags={tagSuggestions}
                    onAssign={bulkAssign}
                    onAssignTags={bulkAddTags}
                    onClear={() => setSelectedIds(new Set())}
                    assigning={bulkAssigning}
                    tagging={bulkTagging}
                />
            )}

            {/* Upload Audio Files dialog — rendered as sibling so it can portal out of the layout div */}
            <Dialog
                open={isUploadOpen}
                onOpenChange={(open) => {
                    if (!open && !isUploading) resetUploadDialog();
                    setIsUploadOpen(open);
                }}
            >
                <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="rounded-full bg-blue-100 p-2">
                                <UploadIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                                <DialogTitle>Upload for AI Quality Monitoring</DialogTitle>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Step {uploadStep} of {uploadTotalSteps} ·{" "}
                                    {uploadStep === 1
                                        ? "Choose files"
                                        : uploadStep === 2
                                          ? "Configure scoring rubric"
                                          : "Add knowledge documents"}
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    {/* Step body — scrolls when content overflows */}
                    <div className="flex-1 overflow-y-auto py-2 -mx-6 px-6">
                        {uploadStep === 1 ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Left panel — drag & drop zone */}
                                <div className="flex flex-col">
                                    <div
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => !isUploading && fileInputRef.current?.click()}
                                        className={cn(
                                            "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 flex-1 text-center transition-colors cursor-pointer min-h-[220px]",
                                            isDragging
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-300 hover:border-blue-400 hover:bg-gray-50",
                                            isUploading && "pointer-events-none opacity-60",
                                        )}
                                    >
                                        <UploadIcon className="w-12 h-12 mb-4 text-gray-300" />
                                        <p className="text-base font-semibold text-gray-700 mb-1">
                                            Drag & drop audio or chat logs here
                                        </p>
                                        <p className="text-sm text-gray-500 mb-5">
                                            or click to browse files
                                        </p>
                                        <div className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50 transition-colors pointer-events-none">
                                            <UploadIcon className="w-4 h-4" />
                                            Browse Files
                                        </div>
                                        <p className="text-xs text-gray-400 mt-4">
                                            MP3, WAV, M4A, TXT &middot; Up to {formatFileSize(MAX_FILE_SIZE)} each &middot; Max {MAX_FILES} files &middot; {formatFileSize(MAX_TOTAL_UPLOAD_SIZE)} total
                                        </p>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept={ACCEPTED_EXTENSIONS.join(",")}
                                            onChange={(e) => {
                                                if (e.target.files) addFiles(e.target.files);
                                                e.target.value = "";
                                            }}
                                            className="hidden"
                                        />
                                    </div>
                                </div>

                                {/* Right panel — queued file list. Cap its height so a long
                                    queue scrolls INSIDE the list instead of stretching the
                                    drop zone + growing the whole dialog (100-file case). */}
                                <div className="flex flex-col min-h-0 lg:max-h-[480px]">
                                    <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                        <h4 className="font-semibold text-base text-gray-900">
                                            Queued Files
                                        </h4>
                                        {hasFiles && (
                                            <span className={cn(
                                                "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
                                                isBatchTooLarge
                                                    ? "bg-rose-100 text-rose-700"
                                                    : "bg-blue-100 text-blue-700",
                                            )}>
                                                {formatFileSize(totalQueuedSize)} / {formatFileSize(MAX_TOTAL_UPLOAD_SIZE)}
                                            </span>
                                        )}
                                    </div>
                                    {isBatchTooLarge && (
                                        <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 mb-3 flex-shrink-0">
                                            <AlertCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                            Total size {formatFileSize(totalQueuedSize)} exceeds the {formatFileSize(MAX_TOTAL_UPLOAD_SIZE)} batch limit. Remove some files to continue.
                                        </div>
                                    )}

                                    {!hasFiles ? (
                                        <div className="flex-1 flex items-center justify-center rounded-lg border border-dashed border-gray-200 p-8 min-h-[180px]">
                                            <div className="text-center">
                                                <FileAudioIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                                <p className="text-sm text-gray-500">No files added yet.</p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Add up to {MAX_FILES} files to continue.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1 overflow-y-auto min-h-0 border border-gray-200 rounded-lg">
                                            {files.map((entry, idx) => (
                                                <div
                                                    key={entry.id}
                                                    className={cn(
                                                        "flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors",
                                                        idx !== files.length - 1 && "border-b border-gray-100",
                                                    )}
                                                >
                                                    <FileAudioIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-800 truncate">
                                                            {entry.file.name}
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            {formatFileSize(entry.file.size)}
                                                        </p>
                                                        {entry.errorMessage && (
                                                            <p className="text-xs text-rose-500 mt-0.5">
                                                                {entry.errorMessage}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {entry.status === "error" ? (
                                                        <AlertCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                                    ) : (
                                                        <CheckCircle2Icon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                                    )}
                                                    <button
                                                        onClick={() => removeFile(entry.id)}
                                                        className="p-1 text-gray-400 hover:text-gray-600 rounded flex-shrink-0"
                                                    >
                                                        <XIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : uploadStep === 2 ? (
                            <div className="space-y-5">
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600">
                                        Configure how this call will be scored for your uploaded files.
                                    </p>
                                    <RubricEditor
                                        value={rubricCriteria}
                                        onChange={setRubricCriteria}
                                        optionsTemplate={
                                            isSuperbank ? SUPERBANK_LIVECHAT_TEMPLATE : undefined
                                        }
                                        showOnlyWeights
                                    />
                                </div>

                                {/* Tags — group these calls for the Insights filter.
                                    Hidden until the backend persists tags. */}
                                {AIQM_TAGS_ENABLED && (
                                    <div className="space-y-1.5 border-t border-gray-100 pt-4">
                                        <label className="block text-sm font-medium text-gray-900">
                                            Tags{" "}
                                            <span className="font-normal text-gray-400">(optional)</span>
                                        </label>
                                        <p className="text-xs text-gray-500">
                                            Group these calls (e.g. team, location, topic) so you can
                                            filter them later in Insights. Applied to all files in this
                                            upload.
                                        </p>
                                        <TagInput
                                            value={uploadTags}
                                            onChange={setUploadTags}
                                            suggestions={tagSuggestions}
                                            disabled={isUploading}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Step 3 (flag-gated) — knowledge for this upload batch */
                            <div className="space-y-6">
                                <p className="text-sm text-gray-600">
                                    Documents added here will only be used when evaluating this
                                    upload. This step is optional.
                                </p>

                                {/* Section 1 — inline dropzone */}
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                                        Upload documents
                                    </div>
                                    <div
                                        onDragOver={(e) => { e.preventDefault(); setKnowledgeDragging(true); }}
                                        onDragLeave={(e) => { e.preventDefault(); setKnowledgeDragging(false); }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setKnowledgeDragging(false);
                                            if (e.dataTransfer.files.length > 0) addKnowledgeFiles(e.dataTransfer.files);
                                        }}
                                        onClick={() => knowledgeFileInputRef.current?.click()}
                                        className={cn(
                                            "flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-7 text-center transition-colors cursor-pointer",
                                            knowledgeDragging
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-300 hover:border-blue-400 hover:bg-gray-50",
                                        )}
                                    >
                                        <UploadIcon className="w-8 h-8 mb-3 text-gray-300" />
                                        <p className="text-sm font-semibold text-gray-700 mb-0.5">
                                            Drag &amp; drop files here
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            or click to browse · PDF
                                        </p>
                                        <input
                                            ref={knowledgeFileInputRef}
                                            type="file"
                                            multiple
                                            accept=".pdf"
                                            className="hidden"
                                            onChange={(e) => {
                                                if (e.target.files?.length) addKnowledgeFiles(e.target.files);
                                                e.target.value = "";
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Section 2 — everything this upload is evaluated with */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                            Documents included in this upload
                                        </div>
                                        <span className="text-xs text-gray-500 tabular-nums">
                                            {knowledgeFiles.length +
                                                workspaceKnowledgeDocs.filter((d) => !excludedKnowledgeIds.has(d.id)).length}{" "}
                                            included
                                        </span>
                                    </div>
                                    {knowledgeFiles.length === 0 && workspaceKnowledgeDocs.length === 0 ? (
                                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
                                            <p className="text-sm text-gray-500">
                                                No documents yet — upload above, or add workspace-wide
                                                documents from the Knowledge Base tab.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                                            {/* Workspace docs pinned first — the default set;
                                                new uploads for this batch append below them. */}
                                            {workspaceKnowledgeDocs.map((doc) => {
                                                const isExcluded = excludedKnowledgeIds.has(doc.id);
                                                return (
                                                    <div key={doc.id} className={cn("flex items-center gap-3 px-4 py-3", isExcluded && "opacity-50 bg-gray-50")}>
                                                        <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                            <BookOpenIcon className="w-4 h-4 text-blue-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={cn("text-sm font-medium truncate", isExcluded ? "text-gray-500 line-through" : "text-gray-900")}>
                                                                {doc.name}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {doc.fileType} · Workspace-wide
                                                                {isExcluded && " · Removed from this upload"}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => toggleKnowledgeExclusion(doc.id)}
                                                            className={cn(
                                                                "text-xs font-medium flex-shrink-0 px-2 py-1 rounded-md transition-colors",
                                                                isExcluded
                                                                    ? "text-blue-600 hover:bg-blue-50"
                                                                    : "text-gray-400 hover:text-rose-600 hover:bg-rose-50",
                                                            )}
                                                            title={
                                                                isExcluded
                                                                    ? "Include this workspace document again"
                                                                    : "Remove this workspace document from this upload only"
                                                            }
                                                        >
                                                            {isExcluded ? "Include again" : "Remove"}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            {knowledgeFiles.map((file, i) => (
                                                <div key={`${file.name}:${file.size}:${i}`} className="flex items-center gap-3 px-4 py-3">
                                                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                        <UploadIcon className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                            {formatFileSize(file.size)} · New upload · This upload only
                                                        </p>
                                                    </div>
                                                    {/* Same text affordance as the workspace rows —
                                                        one look for the same verb. */}
                                                    <button
                                                        onClick={() =>
                                                            setKnowledgeFiles((prev) => prev.filter((_, j) => j !== i))
                                                        }
                                                        className="text-xs font-medium flex-shrink-0 px-2 py-1 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                        title="Remove"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-[11px] text-gray-400 mt-2">
                                        Workspace-wide documents apply to every upload by default.
                                        Removing one here only affects this upload.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Dialog footer — step-aware actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 flex-shrink-0">
                        {uploadStep === 1 ? (
                            <button
                                onClick={clearFiles}
                                disabled={isUploading || !hasFiles}
                                className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Clear all
                            </button>
                        ) : uploadStep === 2 ? (
                            <button
                                onClick={() => setRubricCriteria(defaultRubric)}
                                disabled={isUploading}
                                className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Reset to defaults
                            </button>
                        ) : (
                            <div />
                        )}
                        <div className="flex gap-2 items-center">
                            {uploadStep === 2 && !isRubricValid && (
                                <span className="text-xs text-amber-600 mr-2">
                                    Weights total {rubricWeightSum}% · adjust to 100%
                                </span>
                            )}
                            {uploadStep === 1 ? (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsUploadOpen(false)}
                                        disabled={isUploading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => setUploadStep(2)}
                                        disabled={!canAdvanceToRubric}
                                    >
                                        Next: Configure rubric
                                    </Button>
                                </>
                            ) : uploadStep === 2 && KB_AIQM_BATCH_KNOWLEDGE_ENABLED ? (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => setUploadStep(1)}
                                        disabled={isUploading}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={() => setUploadStep(3)}
                                        disabled={!isRubricValid}
                                    >
                                        Next: Knowledge
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => setUploadStep(uploadStep === 3 ? 2 : 1)}
                                        disabled={isUploading}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={handleUpload}
                                        disabled={isUploading || !hasQueuedFile || !isRubricValid}
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2Icon className="w-4 h-4 animate-spin mr-1" />
                                                Uploading...
                                            </>
                                        ) : (
                                            "Start Scoring"
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

// --- Sub-components ---

function SessionTableRow({
    session,
    onClick,
    tagsEnabled,
    canRename,
    uploaderEnabled,
    handledByEnabled,
    canWrite,
    allTags,
    trainees,
    selectable,
    selected,
    onToggleSelected,
    onHandledByChange,
}: {
    session: CallSessionListItem;
    onClick: () => void;
    tagsEnabled: boolean;
    canRename: boolean;
    uploaderEnabled: boolean;
    handledByEnabled: boolean;
    // Members (read-only permission) can view but not edit filename/tags/handler.
    canWrite: boolean;
    allTags: string[];
    trainees: AiqmTrainee[];
    // Bulk-select checkbox column (writers only, when handled-by is available).
    selectable: boolean;
    selected: boolean;
    onToggleSelected: () => void;
    // Keep the parent list's handled_by in sync after an inline assign.
    onHandledByChange: (handledBy: AiqmTrainee | null) => void;
}) {
    const chip = STATUS_CHIP[session.status] ?? { label: session.status, pill: "bg-gray-50 text-gray-600 border-gray-200" };
    const grade = session.overall_grade ? GRADE_BADGE[session.overall_grade] : null;
    const formattedScore =
        typeof session.overall_score === "number"
            ? Number.isInteger(session.overall_score)
                ? String(session.overall_score)
                : session.overall_score.toFixed(1)
            : null;

    return (
        <tr
            className={cn(
                "hover:bg-gray-50 cursor-pointer align-middle transition-colors",
                selected && "bg-blue-50/50",
            )}
            onClick={onClick}
        >
            {/* Bulk-select checkbox — writers only when handled-by is available. */}
            {selectable && (
                <td
                    className="px-4 py-3 w-px text-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <input
                        type="checkbox"
                        aria-label="Select call"
                        checked={selected}
                        onChange={onToggleSelected}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                </td>
            )}
            {/* File Name (inline editable) + Duration */}
            <td
                className="px-4 py-3 w-[340px] max-w-[340px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                        <FileAudioIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <FilenameCell session={session} canRename={canRename && canWrite} />
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                            <ClockIcon className="w-3 h-3" />
                            {formatDurationMMSS(session.duration)}
                        </div>
                    </div>
                </div>
            </td>

            {/* Summary — wraps naturally; wider than before (max-w-sm → max-w-lg)
                using the space freed by the narrow Tags column, but still capped
                so it doesn't push the other columns off-screen. */}
            <td className="px-4 py-3 max-w-lg">
                {session.one_line_summary ? (
                    <p className="text-xs text-gray-500 leading-relaxed">
                        {session.one_line_summary}
                    </p>
                ) : session.status === "failed" && session.error_message ? (
                    <p className="text-xs text-rose-500 leading-relaxed">
                        {session.error_message}
                    </p>
                ) : (
                    <span className="text-sm text-gray-300">—</span>
                )}
            </td>

            {/* Score */}
            <td className="px-4 py-3 w-px whitespace-nowrap text-center">
                {formattedScore !== null ? (
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">
                        {formattedScore}
                        <span className="text-xs font-normal text-gray-400"> / 100</span>
                    </span>
                ) : (
                    <span className="text-sm text-gray-300">—</span>
                )}
            </td>

            {/* Grade */}
            <td className="px-4 py-3 w-px whitespace-nowrap text-center">
                {session.overall_grade ? (
                    <span
                        className={cn(
                            "inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold",
                            grade ? `${grade.bg} ${grade.text}` : "bg-gray-100 text-gray-400",
                        )}
                    >
                        {session.overall_grade}
                    </span>
                ) : (
                    <span className="text-sm text-gray-300">—</span>
                )}
            </td>

            {/* Analyzed date — "17 May 2026 at 4.40PM" */}
            <td className="px-4 py-3 w-px whitespace-nowrap text-center text-xs text-gray-500">
                {formatDateTime(session.created_at)}
            </td>

            {/* Uploaded by — avatar style mirrors the campaign list Team Leader column */}
            {uploaderEnabled && (
                <td className="px-4 py-3 whitespace-nowrap text-center">
                    {session.uploaded_by_name ? (
                        <div className="flex items-center justify-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                {session.uploaded_by_name
                                    .split(" ")
                                    .map((p) => p[0])
                                    .slice(0, 2)
                                    .join("")
                                    .toUpperCase()}
                            </div>
                            <span className="text-sm text-gray-700">{session.uploaded_by_name}</span>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-sm">—</span>
                    )}
                </td>
            )}

            {/* Handled by — inline assign / reassign / unassign (write-gated) */}
            {handledByEnabled && (
                <td
                    className="px-4 py-3 whitespace-nowrap text-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <HandledByCell
                        session={session}
                        trainees={trainees}
                        canEdit={canWrite}
                        onChange={onHandledByChange}
                    />
                </td>
            )}

            {/* Tags — inline editable */}
            {tagsEnabled && (
                <td
                    className="px-4 py-3 w-[180px] align-middle"
                    onClick={(e) => e.stopPropagation()}
                >
                    <TagCell session={session} allTags={allTags} canEdit={canWrite} />
                </td>
            )}

            {/* Status chip */}
            <td className="px-4 py-3 w-[110px] whitespace-nowrap text-center">
                <span
                    className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold",
                        chip.pill,
                    )}
                >
                    {chip.label}
                </span>
            </td>
        </tr>
    );
}

// Inline editable filename for a call row. Click the pencil to edit; Enter or
// blur saves, Escape cancels. Persists via renameCallSession with optimistic
// update + rollback. Falls back to contact name / "Untitled" when no filename.
function FilenameCell({
    session,
    canRename,
}: {
    session: CallSessionListItem;
    canRename: boolean;
}) {
    const initial = session.filename || session.contact_name || "Untitled";
    const [name, setName] = useState(initial);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(initial);
    const [saving, setSaving] = useState(false);

    // Rename endpoint not available in this environment — show read-only so we
    // never surface an edit control that would fail on save.
    if (!canRename) {
        return (
            <span className="text-sm font-medium text-gray-900 break-words">
                {name}
            </span>
        );
    }

    function startEdit() {
        setDraft(name);
        setEditing(true);
    }

    async function save() {
        const trimmed = draft.trim();
        setEditing(false);
        if (!trimmed || trimmed === name) return;
        const prev = name;
        setName(trimmed); // optimistic
        setSaving(true);
        try {
            const saved = await renameCallSession(session.id, trimmed);
            setName(saved);
        } catch (err) {
            setName(prev); // rollback
            catchErrorLog(
                `renameCallSession failed for ${session.id}: ${
                    err instanceof Error ? err.message : String(err)
                }`,
            );
        } finally {
            setSaving(false);
        }
    }

    if (editing) {
        return (
            <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={save}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        save();
                    } else if (e.key === "Escape") {
                        setEditing(false);
                    }
                }}
                className="w-full rounded border border-blue-400 px-1.5 py-0.5 text-sm font-medium outline-none ring-1 ring-blue-100"
            />
        );
    }

    return (
        <div className="group flex items-center gap-1">
            <span className="text-sm font-medium text-gray-900 break-words">
                {name}
            </span>
            <button
                type="button"
                onClick={startEdit}
                className="flex-shrink-0 text-gray-300 opacity-0 transition-opacity hover:text-gray-600 group-hover:opacity-100"
                aria-label="Rename file"
            >
                <PencilIcon className="h-3.5 w-3.5" />
            </button>
            {saving && <Loader2Icon className="h-3 w-3 animate-spin text-gray-300" />}
        </div>
    );
}

// Inline editable tag cell for a call row. Shows tag chips; clicking opens a
// popover with the chip input to add/remove. Persists via updateCallTags and
// updates local state optimistically (the row's parent list isn't refetched).
function TagCell({
    session,
    allTags,
    canEdit,
}: {
    session: CallSessionListItem;
    allTags: string[];
    canEdit: boolean;
}) {
    const [tags, setTags] = useState<string[]>(session.tags ?? []);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // Read-only permission (member): show existing tags as plain chips with no
    // add/remove affordance — no popover trigger so tags can't be edited.
    if (!canEdit) {
        return (
            <div className="flex max-w-[160px] flex-wrap items-center gap-1 px-1.5 py-1">
                {tags.length === 0 ? (
                    <span className="text-sm text-gray-300">—</span>
                ) : (
                    tags.map((t) => (
                        <span
                            key={t}
                            className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700"
                        >
                            {t}
                        </span>
                    ))
                )}
            </div>
        );
    }

    async function commit(next: string[]) {
        const prev = tags;
        setTags(next); // optimistic
        setSaving(true);
        try {
            const saved = await updateCallTags(session.id, next);
            setTags(saved);
        } catch (err) {
            setTags(prev); // rollback
            catchErrorLog(
                `updateCallTags failed for ${session.id}: ${
                    err instanceof Error ? err.message : String(err)
                }`,
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="flex max-w-[160px] flex-wrap items-center gap-1 rounded-md px-1.5 py-1 text-left hover:bg-gray-100"
                >
                    {tags.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <TagIcon className="h-3.5 w-3.5" />
                            Add tags
                        </span>
                    ) : (
                        tags.map((t) => (
                            <span
                                key={t}
                                className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700"
                            >
                                {t}
                            </span>
                        ))
                    )}
                    {saving && <Loader2Icon className="h-3 w-3 animate-spin text-gray-300" />}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
                <p className="mb-1.5 text-xs font-medium text-gray-500">
                    Tags for this call
                </p>
                <TagInput
                    value={tags}
                    onChange={commit}
                    suggestions={allTags}
                    placeholder="Add a tag…"
                />
            </PopoverContent>
        </Popover>
    );
}

// Two-letter initials for an avatar bubble (mirrors the Uploaded-by cell).
function initials(name: string): string {
    return name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

// Inline "Handled by" assign cell for a call row. Read-only members see the
// assigned trainee (or —) as plain text with no menu. Writers get a popover to
// assign / reassign / unassign. Persists via assignCallHandler with optimistic
// update + rollback, and keeps the parent list in sync via onChange.
function HandledByCell({
    session,
    trainees,
    canEdit,
    onChange,
}: {
    session: CallSessionListItem;
    trainees: AiqmTrainee[];
    canEdit: boolean;
    onChange: (handledBy: AiqmTrainee | null) => void;
}) {
    const [handler, setHandler] = useState<AiqmTrainee | null>(session.handled_by ?? null);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // Keep local state in sync if the parent row data changes (e.g. bulk assign).
    useEffect(() => {
        setHandler(session.handled_by ?? null);
    }, [session.handled_by]);

    // Read-only (member): show the handler name or — with no assign affordance.
    if (!canEdit) {
        return handler ? (
            <div className="flex items-center justify-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {initials(handler.name)}
                </div>
                <span className="text-sm text-gray-700">{handler.name}</span>
            </div>
        ) : (
            <span className="text-gray-400 text-sm">—</span>
        );
    }

    async function assign(traineeId: string | null) {
        setOpen(false);
        const prev = handler;
        const next = traineeId
            ? (trainees.find((t) => t.id === traineeId) ?? { id: traineeId, name: traineeId })
            : null;
        if ((next?.id ?? null) === (prev?.id ?? null)) return;
        setHandler(next); // optimistic
        onChange(next);
        setSaving(true);
        try {
            const saved = await assignCallHandler(session.id, traineeId);
            // BE may echo a normalized trainee; trust it when present, else keep
            // the optimistic value (mock/echo-less responses).
            const resolved = saved ?? next;
            setHandler(resolved);
            onChange(resolved);
        } catch (err) {
            setHandler(prev); // rollback
            onChange(prev);
            catchErrorLog(
                `assignCallHandler failed for ${session.id}: ${
                    err instanceof Error ? err.message : String(err)
                }`,
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-md px-2 py-1 hover:bg-gray-100"
                >
                    {handler ? (
                        <>
                            <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                {initials(handler.name)}
                            </div>
                            <span className="text-sm text-gray-700">{handler.name}</span>
                        </>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <UserPlusIcon className="h-3.5 w-3.5" />
                            Assign
                        </span>
                    )}
                    {saving && <Loader2Icon className="h-3 w-3 animate-spin text-gray-300" />}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-1" align="center">
                <p className="px-2 py-1.5 text-xs font-medium text-gray-500">
                    Assign handler
                </p>
                <div className="max-h-64 overflow-y-auto">
                    {trainees.length === 0 ? (
                        <p className="px-2 py-2 text-xs text-gray-400">
                            No assignable employees yet
                        </p>
                    ) : (
                        trainees.map((t) => {
                            const active = handler?.id === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => assign(t.id)}
                                    className={cn(
                                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50",
                                        active && "bg-blue-50",
                                    )}
                                >
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                                        {initials(t.name)}
                                    </div>
                                    <span className="flex-1 truncate text-gray-700">{t.name}</span>
                                    {active && <UserCheckIcon className="h-3.5 w-3.5 text-blue-600" />}
                                </button>
                            );
                        })
                    )}
                </div>
                {handler && (
                    <button
                        type="button"
                        onClick={() => assign(null)}
                        className="mt-1 flex w-full items-center gap-2 rounded-md border-t border-gray-100 px-2 py-1.5 text-left text-sm text-rose-600 hover:bg-rose-50"
                    >
                        <XIcon className="h-3.5 w-3.5" />
                        Unassign
                    </button>
                )}
            </PopoverContent>
        </Popover>
    );
}

// Tag multi-select filter for the Calls tab. Follows the house dropdown-filter
// recipe (Section 6a): "All tags (total)" first, per-option counts, hide-zero.
// Built on a Popover (Radix Select is single-value) but styled to match the
// shared Select triggers and the sentiment-tab tag multi-select.
// Compact single-select pill — used for the Grade section of the Filters popover.
function FilterPill({
    active,
    onClick,
    label,
    count,
}: {
    active: boolean;
    onClick: () => void;
    label: string;
    count?: number;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors",
                active
                    ? "bg-blue-600 text-white"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
            )}
        >
            {label}
            {count != null && (
                <span className={cn("ml-1", active ? "text-blue-100" : "text-gray-400")}>
                    {count}
                </span>
            )}
        </button>
    );
}

// Single-select row (name + optional count + check) — used for the Uploaded-by
// and Handled-by sections of the Filters popover.
function FilterRadioRow({
    selected,
    onClick,
    label,
    count,
}: {
    selected: boolean;
    onClick: () => void;
    label: string;
    count?: number;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50",
                selected && "bg-blue-50/60",
            )}
        >
            <span className="truncate text-gray-700">{label}</span>
            <span className="flex flex-shrink-0 items-center gap-1.5">
                {count != null && <span className="text-gray-400">({count})</span>}
                {selected && <CheckIcon className="h-3.5 w-3.5 text-blue-600" />}
            </span>
        </button>
    );
}

function TagFilterOption({
    checked,
    onToggle,
    label,
    count,
    italic = false,
}: {
    checked: boolean;
    onToggle: () => void;
    label: string;
    count: number;
    italic?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50"
        >
            <span
                className={cn(
                    "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border",
                    checked ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300",
                )}
            >
                {checked && (
                    <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2.5 6.5 5 9l4.5-5" />
                    </svg>
                )}
            </span>
            <span className={cn("flex-1 truncate text-gray-700", italic && "italic text-gray-500")} title={label}>
                {label}
            </span>
            <span className="text-gray-400">({count})</span>
        </button>
    );
}

// Two-pane bulk tag picker. The left pane composes the tags to add (removable
// chips + apply); the right pane is a searchable, independently-scrolling list of
// existing tags — kept as a dedicated side panel so it stays comfortable as the
// tag vocabulary grows instead of cramming an inline dropdown. ADD/merge mode.
function BulkTagPopover({
    label,
    allTags,
    onApply,
    applying,
}: {
    label: string;
    allTags: string[];
    onApply: (tags: string[]) => void;
    applying: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [staged, setStaged] = useState<string[]>([]);

    const trimmed = query.trim();
    const filtered = useMemo(
        () =>
            allTags.filter((t) =>
                t.toLowerCase().includes(trimmed.toLowerCase()),
            ),
        [allTags, trimmed],
    );
    const canCreate =
        trimmed.length > 0 &&
        !allTags.some((t) => t.toLowerCase() === trimmed.toLowerCase());

    function toggle(tag: string) {
        setStaged((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
        );
    }
    function addFromQuery() {
        if (!trimmed) return;
        setStaged((prev) =>
            prev.some((t) => t.toLowerCase() === trimmed.toLowerCase())
                ? prev
                : [...prev, trimmed],
        );
        setQuery("");
    }
    function reset() {
        setStaged([]);
        setQuery("");
    }
    function apply() {
        if (staged.length === 0) return;
        const next = staged;
        reset();
        setOpen(false);
        onApply(next);
    }

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <PopoverTrigger asChild>
                <Button size="sm" variant="outline" disabled={applying}>
                    {applying ? (
                        <>
                            <Loader2Icon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            Adding…
                        </>
                    ) : (
                        <>
                            <TagIcon className="mr-1.5 h-3.5 w-3.5" />
                            Assign tags
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-[440px] p-0">
                <div className="flex">
                    {/* Left — compose + apply */}
                    <div className="flex w-[190px] flex-shrink-0 flex-col border-r border-gray-100 p-3">
                        <p className="mb-2 text-xs font-medium text-gray-500">
                            Add tags to {label}
                        </p>
                        <div className="mb-3 flex min-h-[52px] flex-wrap content-start gap-1">
                            {staged.length === 0 ? (
                                <span className="text-xs text-gray-400">
                                    No tags selected yet
                                </span>
                            ) : (
                                staged.map((t) => (
                                    <span
                                        key={t}
                                        className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700"
                                    >
                                        {t}
                                        <button
                                            type="button"
                                            onClick={() => toggle(t)}
                                            className="text-blue-400 hover:text-blue-700"
                                            aria-label={`Remove ${t}`}
                                        >
                                            <XIcon className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))
                            )}
                        </div>
                        <Button
                            size="sm"
                            className="mt-auto w-full"
                            disabled={staged.length === 0 || applying}
                            onClick={apply}
                        >
                            Add to {label}
                        </Button>
                    </div>
                    {/* Right — searchable existing-tag list */}
                    <div className="flex min-w-0 flex-1 flex-col p-2">
                        <div className="relative mb-2">
                            <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                            <Input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addFromQuery();
                                    }
                                }}
                                placeholder="Search or create…"
                                className="h-8 pl-8 text-sm"
                            />
                        </div>
                        <div className="max-h-56 min-h-[7rem] overflow-y-auto">
                            {canCreate && (
                                <button
                                    type="button"
                                    onClick={addFromQuery}
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-blue-700 hover:bg-blue-50"
                                >
                                    <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">Create “{trimmed}”</span>
                                </button>
                            )}
                            {filtered.length === 0 && !canCreate ? (
                                <p className="px-2 py-6 text-center text-xs text-gray-400">
                                    {allTags.length === 0
                                        ? "No tags yet — type to create one"
                                        : "No matching tags"}
                                </p>
                            ) : (
                                filtered.map((t) => {
                                    const selected = staged.includes(t);
                                    return (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => toggle(t)}
                                            className={cn(
                                                "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50",
                                                selected && "bg-blue-50/60",
                                            )}
                                        >
                                            <span className="truncate text-gray-700">{t}</span>
                                            {selected && (
                                                <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

// Bulk "assign handler" picker. Single-select searchable list (search appears
// once the roster grows past a handful) so it scales, right-aligned above the
// bar. Passing null unassigns the handler across all selected calls.
function BulkHandlerPopover({
    label,
    trainees,
    onAssign,
    assigning,
}: {
    label: string;
    trainees: AiqmTrainee[];
    onAssign: (traineeId: string | null) => void;
    assigning: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const trimmed = query.trim().toLowerCase();
    const filtered = useMemo(
        () =>
            trimmed
                ? trainees.filter((t) => t.name.toLowerCase().includes(trimmed))
                : trainees,
        [trainees, trimmed],
    );

    function choose(id: string | null) {
        setOpen(false);
        setQuery("");
        onAssign(id);
    }

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) setQuery("");
            }}
        >
            <PopoverTrigger asChild>
                <Button size="sm" disabled={assigning}>
                    {assigning ? (
                        <>
                            <Loader2Icon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            Assigning…
                        </>
                    ) : (
                        <>
                            <UserPlusIcon className="mr-1.5 h-3.5 w-3.5" />
                            Assign handler
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-64 p-2">
                <p className="mb-2 px-1 text-xs font-medium text-gray-500">
                    Assign {label} to
                </p>
                {trainees.length > 6 && (
                    <div className="relative mb-2">
                        <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <Input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search employees…"
                            className="h-8 pl-8 text-sm"
                        />
                    </div>
                )}
                <div className="max-h-64 overflow-y-auto">
                    {filtered.length === 0 ? (
                        <p className="px-2 py-2 text-xs text-gray-400">
                            {trainees.length === 0
                                ? "No assignable employees yet"
                                : "No matching employees"}
                        </p>
                    ) : (
                        filtered.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => choose(t.id)}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50"
                            >
                                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-semibold text-emerald-700">
                                    {initials(t.name)}
                                </div>
                                <span className="flex-1 truncate text-gray-700">
                                    {t.name}
                                </span>
                            </button>
                        ))
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => choose(null)}
                    className="mt-1 flex w-full items-center gap-2 rounded-md border-t border-gray-100 px-2 py-1.5 text-left text-sm text-rose-600 hover:bg-rose-50"
                >
                    <XIcon className="h-3.5 w-3.5" />
                    Unassign all
                </button>
            </PopoverContent>
        </Popover>
    );
}

// Floating bulk-assign action bar — appears while rows are selected (writers
// only). Composes the shared BulkActionBar shell with the tag + handler pickers.
function BulkAssignBar({
    count,
    showHandler,
    showTags,
    trainees,
    allTags,
    onAssign,
    onAssignTags,
    onClear,
    assigning,
    tagging,
}: {
    count: number;
    showHandler: boolean;
    showTags: boolean;
    trainees: AiqmTrainee[];
    allTags: string[];
    onAssign: (traineeId: string | null) => void;
    onAssignTags: (tags: string[]) => void;
    onClear: () => void;
    assigning: boolean;
    tagging: boolean;
}) {
    const label = `${count} ${count === 1 ? "call" : "calls"}`;
    return (
        <BulkActionBar label={`${label} selected`} onClear={onClear}>
            {showTags && (
                <BulkTagPopover
                    label={label}
                    allTags={allTags}
                    onApply={onAssignTags}
                    applying={tagging}
                />
            )}
            {showHandler && (
                <BulkHandlerPopover
                    label={label}
                    trainees={trainees}
                    onAssign={onAssign}
                    assigning={assigning}
                />
            )}
        </BulkActionBar>
    );
}

function SkeletonTable() {
    return (
        <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-6">
                    <div className="space-y-1.5 w-[180px]">
                        <div className="h-3.5 w-36 bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-14 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="flex-1 space-y-1">
                        <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="h-3.5 w-14 bg-gray-100 rounded animate-pulse" />
                    <div className="h-8 w-8 bg-gray-100 rounded-lg animate-pulse" />
                    <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
                    <div className="h-5 w-20 bg-gray-100 rounded-full animate-pulse" />
                </div>
            ))}
        </div>
    );
}

function SortableTh({
    label,
    sortKey,
    current,
    dir,
    onSort,
    align = "left",
    disabled = false,
    widthClass,
}: {
    label: string;
    sortKey: SortKey;
    current: SortKey;
    dir: SortDir;
    onSort: (key: SortKey) => void;
    align?: "left" | "center";
    disabled?: boolean;
    widthClass?: string;
}) {
    const active = !disabled && current === sortKey;
    const justify = align === "center" ? "justify-center" : "justify-start";
    return (
        <th
            className={cn(
                "h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap",
                !disabled && "cursor-pointer select-none hover:text-gray-800",
                align === "center" ? "text-center" : "text-left",
                widthClass,
            )}
            onClick={() => !disabled && onSort(sortKey)}
        >
            <div className={cn("flex items-center gap-1", justify)}>
                {label}
                {!disabled && (
                    active ? (
                        dir === "asc"
                            ? <ChevronUp className="w-3 h-3" />
                            : <ChevronDown className="w-3 h-3" />
                    ) : (
                        <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )
                )}
            </div>
        </th>
    );
}
